// The static indexed triangle mesh: one CPU-built shape, one VBO, one index
// buffer and one VAO.
//
// The three batches of `quads.ts`, `segments.ts` and `circles.ts` are
// divisor-1: every attribute belongs to a whole instance, and the four
// corners come from the one per-vertex attribute of `corner.ts`. That shape
// draws a mark. It cannot draw a traced outline, because a traced outline
// has a DIFFERENT value at every vertex — the position, the along-distance
// and the side of the stroke — and it shares vertices between triangles.
//
// So the territory OUTLINE border (the plan of 2026-09-04, decision R495)
// needs the divisor-0 sibling that lives here: per-vertex attributes, an
// index buffer, and `drawElements(TRIANGLES)`. The geometry is traced once
// per owner change and then drawn every frame, so it uploads with
// `STATIC_DRAW` and never grows.
//
// Like every other GPU object of the engine, it survives a context loss: the
// two CPU arrays are kept, and `create` builds the GPU objects again from
// them.

import type { GL, GpuResource } from './context';
import type { AttribSpec, MeshBuffer as MeshBufferApi } from '../gfx/types';

/**
 * A static indexed triangle mesh with its VBO, its index buffer and its VAO.
 *
 * `set` replaces the whole geometry. There is no partial write, because the
 * traced border is rebuilt whole every time the owner map changes.
 */
export class MeshBuffer implements GpuResource, MeshBufferApi {
  private vertices: Float32Array | null = null;
  private indices: Uint32Array | null = null;
  private vbo: WebGLBuffer | null = null;
  private ibo: WebGLBuffer | null = null;
  private vao: WebGLVertexArrayObject | null = null;

  constructor(
    private readonly glc: GL,
    readonly floatsPerVertex: number,
    private readonly attribs: readonly AttribSpec[],
  ) {
    glc.register(this);
  }

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();
    const vao = gl.createVertexArray();
    if (!vbo || !ibo || !vao) throw new Error('fast: cannot create a mesh buffer');
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    if (this.vertices) gl.bufferData(gl.ARRAY_BUFFER, this.vertices, gl.STATIC_DRAW);
    const stride = this.floatsPerVertex * 4;
    for (const a of this.attribs) {
      gl.enableVertexAttribArray(a.location);
      gl.vertexAttribPointer(a.location, a.size, gl.FLOAT, false, stride, a.offset * 4);
      // Per VERTEX, not per instance. The VAO can outlive an instanced batch
      // that left the divisor at 1 on the same location, so it is set here.
      gl.vertexAttribDivisor(a.location, 0);
    }
    // The element binding belongs to the VAO, so it is made while the VAO is
    // bound and it is cleared only after the VAO is unbound.
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    if (this.indices) gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
    this.vbo = vbo;
    this.ibo = ibo;
    this.vao = vao;
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.vbo) gl.deleteBuffer(this.vbo);
    if (this.ibo) gl.deleteBuffer(this.ibo);
    if (this.vao) gl.deleteVertexArray(this.vao);
    this.vbo = null;
    this.ibo = null;
    this.vao = null;
  }

  /**
   * Replaces the geometry and uploads it.
   *
   * Both arrays are kept on the instance, so `create` refills the GPU objects
   * after a context restore. A call while the context is lost keeps the
   * arrays and uploads nothing; the restore then does the upload.
   */
  set(vertices: Float32Array, indices: Uint32Array): void {
    this.vertices = vertices;
    this.indices = indices;
    if (this.glc.lost || !this.vao || !this.vbo || !this.ibo) return;
    const gl = this.glc.gl;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
  }

  /** How many indices the last `set` gave. It is 0 before the first one. */
  get indexCount(): number {
    return this.indices ? this.indices.length : 0;
  }

  /**
   * Draws the mesh as one `drawElements`. Returns 1, or 0 when there is
   * nothing to draw or the context is lost.
   */
  draw(): number {
    const n = this.indexCount;
    if (n === 0 || !this.vao || this.glc.lost) return 0;
    const gl = this.glc.gl;
    gl.bindVertexArray(this.vao);
    gl.drawElements(gl.TRIANGLES, n, gl.UNSIGNED_INT, 0);
    gl.bindVertexArray(null);
    this.glc.drawCalls++;
    this.glc.instances++;
    return 1;
  }

  dispose(): void { this.glc.unregister(this); }
}
